import {
  useCallback, useEffect, useRef, useState
} from "react";

/** The field every channel generate-code response shares. */
export interface PairingSessionData {
  expires_at: string;
}

interface StoredPairingSession<T> {
  /** The generate-code response, dropped once its code expires. */
  data: T | null;
  /** ISO instant after which generate-code is allowed again. */
  cooldownUntil: string | null;
}

const EMPTY: StoredPairingSession<never> = { data: null, cooldownUntil: null };

function isFuture(iso: string | null | undefined): boolean {
  if (!iso) return false;
  const time = new Date(iso).getTime();
  return Number.isFinite(time) && time > Date.now();
}

function readStored<T extends PairingSessionData>(
  storageKey: string,
): StoredPairingSession<T> {
  try {
    const raw = sessionStorage.getItem(storageKey);
    if (!raw) return EMPTY;
    const parsed = JSON.parse(raw) as StoredPairingSession<T> | null;
    const data = parsed?.data ?? null;
    const cooldownUntil = parsed?.cooldownUntil ?? null;
    return {
      data: data && isFuture(data.expires_at) ? data : null,
      cooldownUntil: isFuture(cooldownUntil) ? cooldownUntil : null,
    };
  } catch {
    return EMPTY;
  }
}

function writeStored<T>(
  storageKey: string,
  session: StoredPairingSession<T>,
): void {
  try {
    if (!session.data && !session.cooldownUntil) {
      sessionStorage.removeItem(storageKey);
      return;
    }
    sessionStorage.setItem(storageKey, JSON.stringify(session));
  } catch {
    // Private-mode and quota failures must never break pairing; the modal just
    // falls back to in-memory state for this open.
  }
}

/**
 * A channel-pairing attempt that outlives the modal.
 *
 * The verification code arrives out of band, so users routinely close the modal
 * to go read it in Messages. Holding the pending attempt in `sessionStorage`
 * means reopening — or reloading — lands them back on code entry, instead of a
 * blank form whose only button the server blocks for the next ten minutes.
 *
 * The channel's generate-code response and expiry are stored in this tab. SMS
 * and iMessage responses contain the target identifier; WhatsApp also contains
 * the verification code and deep link that the modal already renders. All
 * stored data is origin-scoped and discarded after the response expires.
 *
 * `storageKey` is expected to carry the signed-in user, and sessionStorage
 * outlives a logout within the same tab — so the key can change under us, both
 * when auth resolves after mount and when a second user signs in. Re-read on
 * every change, or one user inherits the other's pending identifier.
 */
export function usePairingSession<T extends PairingSessionData>(
  storageKey: string,
) {
  const [session, setSession] = useState<StoredPairingSession<T>>(() =>
    readStored<T>(storageKey),
  );

  const loadedKey = useRef(storageKey);
  useEffect(() => {
    if (loadedKey.current === storageKey) return;
    loadedKey.current = storageKey;
    setSession(readStored<T>(storageKey));
  }, [storageKey]);

  const update = useCallback(
    (next: StoredPairingSession<T>) => {
      writeStored(storageKey, next);
      setSession(next);
    },
    [storageKey],
  );

  const saveCode = useCallback(
    (data: T) => update({ ...session, data }),
    [session, update],
  );

  /** Drop the pending code but keep the cooldown — it is still running. */
  const clearCode = useCallback(
    () => update({ ...session, data: null }),
    [session, update],
  );

  const startCooldown = useCallback(
    (seconds: number) =>
      update({
        ...session,
        cooldownUntil: new Date(Date.now() + seconds * 1000).toISOString(),
      }),
    [session, update],
  );

  const clearAll = useCallback(
    () => update({ data: null, cooldownUntil: null }),
    [update],
  );

  return {
    data: session.data,
    cooldownUntil: session.cooldownUntil,
    saveCode,
    clearCode,
    startCooldown,
    clearAll,
  };
}
